/**
 * DelegatedTasksPage — staff-facing list of tasks the ADMIN delegated to me
 * from the CloudGM "إدراج مهام" (Manual Tasks) hub.
 *
 * Wraps `GET /api/admin/assigned-tasks/mine` (which already returns tasks
 * where assigned_to_id or assigned_to_email equals the current user).
 *
 * Row click → the same task-detail page used by the admin
 *            (/dashboard/cloudgm/assigned-tasks/:id), which lets the staff
 * member update stages, add actions, transfer, and submit for closure/audit.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Inbox, RefreshCw, User, Store, Clock, AlertCircle,
  CheckCircle2, XCircle, ChevronRight, ClipboardCheck,
} from 'lucide-react';

import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:     { ar: 'قيد الانتظار', en: 'Pending',     color: '#f59e0b', Icon: Clock },
  in_progress: { ar: 'قيد التنفيذ',  en: 'In progress', color: '#3b82f6', Icon: AlertCircle },
  completed:   { ar: 'مكتملة',       en: 'Completed',   color: '#10b981', Icon: CheckCircle2 },
  cancelled:   { ar: 'ملغاة',        en: 'Cancelled',   color: '#ef4444', Icon: XCircle },
};

export default function DelegatedTasksPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');   // active | pending | completed | all | audit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'pending') params.status = 'pending';
      else if (filter === 'completed') params.status = 'completed';
      const r = await axios.get(`${API_URL}/api/admin/assigned-tasks/mine`, {
        headers: getAuthHeaders(), params,
      });
      let data = r.data.data || [];
      // Client-side filters for statuses that aren't cleanly represented
      if (filter === 'active') {
        data = data.filter(x => x.status === 'pending' || x.status === 'in_progress');
      } else if (filter === 'audit') {
        data = data.filter(x => x.review_status === 'ready_for_review');
      }
      setRows(data);
    } catch {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [filter, isAr]);

  useEffect(() => { load(); }, [load]);

  const counts = {
    total:    rows.length,
    pending:  rows.filter(x => x.status === 'pending').length,
    inProg:   rows.filter(x => x.status === 'in_progress').length,
  };

  return (
    <DashboardLayout
      title="المهام الموكلة"
      titleEn="Delegated Tasks"
      subtitle="المهام التي أوكلها الأدمن إليك من مركز إدراج مهام العملاء والوكلاء"
      subtitleEn="Tasks delegated to you by the admin from the manual-tasks hub"
      screenCode="EMP-TASK-DEL"
      backPath="/tasks-hub"
      onBack={() => navigate('/tasks-hub')}
      breadcrumb={[
        { labelAr: 'لوحة تحكم الموظف', labelEn: 'Employee Dashboard', path: '/employee-dashboard' },
        { labelAr: 'المهام', labelEn: 'Tasks', path: '/tasks-hub' },
        { labelAr: 'الموكلة', labelEn: 'Delegated' },
      ]}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="delegated-tasks-page">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatCard label={t('كل الموكلة', 'All delegated')} value={counts.total} color="#8B5CF6" />
          <StatCard label={t('قيد الانتظار', 'Pending')}     value={counts.pending} color="#f59e0b" />
          <StatCard label={t('قيد التنفيذ', 'In progress')}  value={counts.inProg} color="#3b82f6" />
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <FilterBtn active={filter === 'active'}     onClick={() => setFilter('active')}     label={t('نشطة', 'Active')} testid="filter-active" />
          <FilterBtn active={filter === 'pending'}    onClick={() => setFilter('pending')}    label={t('قيد الانتظار', 'Pending')} testid="filter-pending" />
          <FilterBtn active={filter === 'audit'}      onClick={() => setFilter('audit')}      label={t('في التدقيق', 'In audit')} testid="filter-audit" />
          <FilterBtn active={filter === 'completed'}  onClick={() => setFilter('completed')}  label={t('مكتملة', 'Completed')} testid="filter-completed" />
          <FilterBtn active={filter === 'all'}        onClick={() => setFilter('all')}        label={t('الكل', 'All')} testid="filter-all" />
          <Button variant="outline" onClick={load}
                  className="ms-auto border-white/10 bg-transparent text-white/80 hover:bg-white/5 h-9">
            <RefreshCw className={`w-3.5 h-3.5 me-1 ${loading ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {/* List */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-white/40" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-16 text-center">
              <Inbox className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/50 text-sm">
                {t('لا مهام موكلة إليك حالياً.', 'No delegated tasks assigned to you right now.')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {rows.map(r => (
                <RowItem key={r.id}
                         row={r}
                         onOpen={() => navigate(`/dashboard/cloudgm/assigned-tasks/${r.id}`)}
                         t={t} isAr={isAr} />
              ))}
            </div>
          )}
        </div>
      </section>
    </DashboardLayout>
  );
}

function FilterBtn({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick}
            data-testid={testid}
            className={`px-4 h-9 rounded-lg text-xs font-medium border transition ${
              active
                ? 'bg-[#d4af37]/20 border-[#d4af37]/60 text-[#d4af37]'
                : 'bg-white/[0.02] border-white/10 text-white/60 hover:text-white hover:border-white/20'
            }`}>
      {label}
    </button>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-xs text-white/50">{label}</span>
      </div>
      <div className="text-xl font-bold text-white">{value}</div>
    </div>
  );
}

function RowItem({ row, onOpen, t, isAr }) {
  const status = STATUS_META[row.status] || STATUS_META.pending;
  const isCustomer = row.target_type === 'customer';
  const accent = isCustomer ? '#3b82f6' : '#8b5cf6';
  const service = isAr ? row.service_name_ar : (row.service_name_en || row.service_name_ar);
  const StatusIcon = status.Icon;

  return (
    <div
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      className="p-4 hover:bg-white/[0.03] transition-colors cursor-pointer"
      data-testid={`delegated-task-row-${row.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
             style={{ backgroundColor: `${accent}20`, border: `1px solid ${accent}40` }}>
          {isCustomer ? <User className="w-5 h-5 text-blue-400" /> : <Store className="w-5 h-5 text-purple-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {row.request_no_display && (
              <span className="text-[11px] font-mono px-2 py-0.5 rounded-md border"
                    style={{ backgroundColor: `${accent}20`, borderColor: `${accent}40`, color: accent }}>
                {row.request_no_display}
              </span>
            )}
            <h3 className="text-white font-semibold text-sm">{service}</h3>
            {row.review_status === 'ready_for_review' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-[#d4af37]/20 text-[#d4af37] border border-[#d4af37]/40 inline-flex items-center gap-1">
                <ClipboardCheck className="w-3 h-3" />
                {t('في التدقيق', 'In audit')}
              </span>
            )}
          </div>
          <div className="text-white/60 text-xs">
            {t('المستهدف', 'Target')}: <span className="text-white">{row.target_name}</span>
          </div>
          {row.request_note && (
            <div className="text-white/50 text-xs mt-1 line-clamp-1">{row.request_note}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs px-2.5 py-1 rounded-md border flex items-center gap-1"
                style={{ color: status.color, borderColor: `${status.color}55`, backgroundColor: `${status.color}15` }}>
            <StatusIcon className="w-3 h-3" />
            {isAr ? status.ar : status.en}
          </span>
          <ChevronRight className="w-4 h-4 text-white/30" />
        </div>
      </div>
    </div>
  );
}
